/**
 * client.ts — typed API client for the backoffice BFF.
 *
 * Every request targets the BFF's `/api/*` surface (same-origin: the BFF serves
 * this bundle AND the API — single deployable, ADR-056) and carries the Entra
 * bearer token in the `Authorization` header. The token getter is injected so
 * the client is testable without MSAL (the real getter is `acquireApiToken`
 * from `auth/useApiToken`).
 *
 * 401/403 are surfaced as a typed `UnauthorizedError` so the shell can render
 * the clean "not authorized" state (or bounce to login) rather than a broken
 * page — never swallow auth failures into a generic error.
 */

import type {
  ActivateSkillVersionRequest,
  ConversationDetail,
  ConversationControlResult,
  ConversationListEnvelope,
  ControlRequest,
  CreateProductRequest,
  CreateSkillVersionRequest,
  CreateSkillVersionResponse,
  DecisionRow,
  FunnelStage,
  HealthOverview,
  HealthTrends,
  LeadDetail,
  LeadFacts,
  LeadFormOptions,
  LeadListEnvelope,
  LeadStatus,
  LeadTransitionRequest,
  LeadTransitionResponse,
  ManualLeadCreateRequest,
  ManualLeadCreateResponse,
  MessageRow,
  ProductConfig,
  ResetLeadRequest,
  ResetLeadResponse,
  SetTenantNumberRequest,
  SkillKey,
  SkillVersionDetail,
  SkillVersionList,
  SourceKey,
  SourceSnapshot,
  TenantNumberConfig,
  TenantProductAvailability,
  TraceListEnvelope,
} from './types';
import { ApiError } from './apiError';
import { UnauthorizedError } from './unauthorizedError';

/** Returns a fresh BFF access token, or throws if one cannot be acquired. */
export type TokenGetter = () => Promise<string>;

export { ApiError } from './apiError';
export { UnauthorizedError } from './unauthorizedError';

/**
 * The list-query inputs the SPA sends as the `POST /api/leads/search` body
 * (ADR-089). `q` is personal data (name, CPF or phone): it travels in the body
 * only, never in a URL, storage or log.
 */
export interface LeadsQuery {
  q?: string | null;
  status?: LeadStatus[];
  product_code?: string | null;
  channel?: string | null;
  tenant_id?: string | null;
  /** ISO date YYYY-MM-DD. */
  created_from?: string | null;
  created_to?: string | null;
  status_from?: string | null;
  status_to?: string | null;
  cursor?: string | null;
  /** 1–100; the BFF defaults to 25. */
  limit?: number;
}

/**
 * The conversation-list inputs the SPA sends (story CONV7). Mirrors `LeadsQuery`
 * shape: a repeated `funnel_stage` multi-select, two boolean toggles, and the
 * opaque keyset `cursor` + `limit`. NO number-search in v1 (deferred per spec).
 *
 * NOTE (wire param names): the BFF read routes (CONV5) land in parallel and the
 * Conversations contract (CONV2) froze only the RESPONSE DTOs, not a filter model.
 * These param names — `funnel_stage` (repeated), `has_error`, `paused`, `cursor`,
 * `limit` — are the SPA's side of that seam and are reconciled at the CONV9 gate.
 */
export interface ConversationsQuery {
  funnel_stage?: FunnelStage[];
  /** Only the threads with a delivery fault (stuck/error). Sent only when true. */
  has_error?: boolean;
  /** Only the paused threads. Sent only when true. */
  paused?: boolean;
  cursor?: string | null;
  /** 1–100; the BFF defaults to 25. */
  limit?: number;
}

/**
 * The product scope the detail/create/activate routes are addressed under.
 * `tenant_id` is deliberately ABSENT — the BFF fills it server-side from
 * `BackofficeConfig.capital_service_tenant_id` and no longer accepts a
 * client-supplied value (E0-BO-CFG-S3 / PR #540). `product_code` is
 * `string | null` because it originates from `SkillKey.product_code`, which
 * is genuinely nullable in the registry — a `null` is OMITTED from the
 * query string entirely, never sent as the literal `"null"` or `""`.
 */
export interface SkillVersionsQuery {
  product_code: string | null;
}

/**
 * `GET /api/config/skills` list-query inputs. Unlike detail/create/activate
 * (where `skill_name` is a URL path segment), the list route takes
 * `skill_name` as a SECOND required query param alongside product_code
 * (verified against `services/backoffice/app/routes/skill_config.py` on
 * PR #534/#540) — the brief omitted it. There is no cross-skill browse; the
 * list is scoped to one skill's version history.
 *
 * `is_active`/`config_version_prefix`/`sort`/`limit`/`offset` back the
 * version-list filters, sort, and pagination (E0-BO-CFG-ACTIVATE-EXISTING-
 * VERSION-AND-AGENT-CONFIG-EDITOR). `undefined` omits the param — the route
 * applies its own defaults (`sort=created_at_desc`, `limit=20`, `offset=0`).
 */
export interface SkillVersionsListQuery extends SkillVersionsQuery {
  skill_name: string;
  is_active?: boolean;
  config_version_prefix?: string;
  sort?: 'created_at_asc' | 'created_at_desc';
  limit?: number;
  offset?: number;
}

export interface ApiClient {
  getHealthOverview(signal?: AbortSignal): Promise<HealthOverview>;
  getHealthSource(source: SourceKey, signal?: AbortSignal): Promise<SourceSnapshot>;
  getHealthTrends(signal?: AbortSignal): Promise<HealthTrends>;
  searchLeads(query: LeadsQuery, signal?: AbortSignal): Promise<LeadListEnvelope>;
  /** Full PII-minimised lead record (LS8). 404 → `ApiError` with status 404. */
  getLeadDetail(leadId: string, signal?: AbortSignal): Promise<LeadDetail>;
  /**
   * Facts Mari captured for this lead (E12-S5.3, ADR-084 D5). Server-ordered
   * (vocabulary order, then unknown keys) — the caller renders as received.
   * 404 → `ApiError` status 404; upstream failure → `ApiError` status 502.
   */
  getLeadFacts(leadId: string, signal?: AbortSignal): Promise<LeadFacts>;
  /**
   * Apply an operator state transition (LS8 write). `actor` is set server-side
   * and is NOT part of the request contract — never sent. The BFF preserves the
   * upstream write semantics distinctly: 422 (illegal edge), 409 (lost race),
   * 404 (unknown lead), 502 (service unavailable) all surface as a typed
   * `ApiError` carrying that exact status + the BFF's `detail` message.
   */
  transitionLead(
    leadId: string,
    body: LeadTransitionRequest,
    signal?: AbortSignal,
  ): Promise<LeadTransitionResponse>;
  /** Keyset-paginated, most-recently-active conversation list (CONV7). */
  getConversations(
    query: ConversationsQuery,
    signal?: AbortSignal,
  ): Promise<ConversationListEnvelope>;
  /**
   * Full operator-facing conversation record (CONV8). 404 → `ApiError` status 404.
   * The `thread_id` is URL-safe by contract (base64url) but we encode it anyway.
   */
  getConversationDetail(threadId: string, signal?: AbortSignal): Promise<ConversationDetail>;
  /**
   * "Todas as mensagens" ledger (Fowler's design doc, Slice 2) — unmasked,
   * chronological. Unknown/cross-tenant thread → `ApiError` status 404;
   * unavailable upstream → `ApiError` status 502.
   */
  getConversationMessages(threadId: string, signal?: AbortSignal): Promise<MessageRow[]>;
  /**
   * "Auditoria" decision ledger (decision-ledger design spec §8) — newest 20
   * turns, no query parameters. Unknown/cross-tenant thread → `ApiError` 404;
   * unavailable upstream → `ApiError` 502.
   */
  getConversationDecisions(threadId: string, signal?: AbortSignal): Promise<DecisionRow[]>;
  /**
   * MLflow traces for a thread (Fowler's design doc, "MLflow design", Slice
   * 6). Unknown/cross-tenant thread → `ApiError` 404; MLflow not configured
   * (`MLFLOW_TRACKING_URI` unset) → `ApiError` 503 with a stable
   * `error_code: "mlflow_not_configured"` body; failing MLflow query →
   * `ApiError` 502.
   */
  getConversationTraces(threadId: string, signal?: AbortSignal): Promise<TraceListEnvelope>;
  /**
   * Apply a pause/resume control action (CONV8). `actor` is set server-side.
   * 409 (concurrent change), 422 (invalid action), 404 (unknown thread), 502
   * (upstream unavailable) all surface as typed `ApiError` with exact status +
   * the BFF's `detail` message. Requeue is NOT a live route in v1 (ADR-058
   * Addendum C) — this client method must never be called for requeue.
   */
  controlConversation(
    threadId: string,
    body: ControlRequest,
    signal?: AbortSignal,
  ): Promise<ConversationControlResult>;
  /**
   * Combined "reset lead" action (CONV11c, ADR-079 D4/D5) — the SOLE reset
   * trigger, replacing the old gated per-thread control. Funnel reset then
   * conversation reset, funnel first, no saga. A 404 (unknown lead) or 502
   * (upstream outage) surfaces as a typed `ApiError`; every other outcome —
   * including the funnel 409-conflict no-op-abort case — is a 200
   * `ResetLeadResponse` the caller renders per-half (AC2), never off HTTP
   * status alone.
   */
  resetLead(
    threadId: string,
    body: ResetLeadRequest,
    signal?: AbortSignal,
  ): Promise<ResetLeadResponse>;
  /** The form's select-input source (LS12): tenants, products, lawful bases. */
  getLeadFormOptions(signal?: AbortSignal): Promise<LeadFormOptions>;
  /**
   * Operator-initiated manual lead creation (LS12). No `actor`/`operator` field
   * is ever sent — the BFF reads identity from the authenticated principal.
   * 422 (validation), 409 (duplicate — `ApiError.body` carries
   * `existing_lead_id`), and 502 (upstream unavailable) surface as a typed
   * `ApiError` with that exact status.
   */
  createLead(
    body: ManualLeadCreateRequest,
    signal?: AbortSignal,
  ): Promise<ManualLeadCreateResponse>;
  /**
   * The real (skill_name, product_code) pairs in the registry — backs the
   * panel's two lookup selectors (story E0-BO-CFG-S3). No query params: the
   * BFF scopes to the JWT tenant claim server-side.
   */
  getSkillKeys(signal?: AbortSignal): Promise<SkillKey[]>;
  /**
   * Paginated/filtered/sorted skill config versions for one skill/product
   * (story E0-BO-CFG-SKILL-REGISTRY-ADMIN + ACTIVATE-EXISTING-VERSION).
   */
  getSkillVersions(query: SkillVersionsListQuery, signal?: AbortSignal): Promise<SkillVersionList>;
  /** Full version record, including the read-only graph_config_json. 404 → ApiError status 404. */
  getSkillVersionDetail(
    skillName: string,
    configVersion: string,
    query: SkillVersionsQuery,
    signal?: AbortSignal,
  ): Promise<SkillVersionDetail>;
  /**
   * Create a new INACTIVE version from a base version. `graph_config_json`
   * is never sent — the server copies it from `base_config_version`. On a
   * 422, `ApiError.body` carries the field-level `{field_path, reason}[]`.
   */
  createSkillVersion(
    skillName: string,
    body: CreateSkillVersionRequest,
    signal?: AbortSignal,
  ): Promise<CreateSkillVersionResponse>;
  /**
   * Activate a version — a deliberate, distinct action from creating one.
   * Returns the now-active version's full detail (the route's response_model
   * is `SkillVersionDetail`, not an empty body).
   */
  activateSkillVersion(
    skillName: string,
    configVersion: string,
    body: ActivateSkillVersionRequest,
    signal?: AbortSignal,
  ): Promise<SkillVersionDetail>;
  /** The configured tenant's WhatsApp number wiring + live status badge (story N1). */
  getNumberConfig(signal?: AbortSignal): Promise<TenantNumberConfig>;
  /**
   * Set/change/clear the configured tenant's `phone_number_id` (story N2).
   * `phone_number_id: null` clears it. On a 422, `ApiError.body` carries
   * `{ detail: {field_path, reason}[] }` (never flattened); on a 409,
   * `ApiError.detail` is a string naming the conflicting tenant.
   */
  setNumberConfig(body: SetTenantNumberRequest, signal?: AbortSignal): Promise<TenantNumberConfig>;
  /** The full credit-product catalogue (Story N3 AC1), not tenant-scoped. */
  getProductCatalogue(signal?: AbortSignal): Promise<ProductConfig[]>;
  /**
   * Register a new product code (Story N5). On a 409 (duplicate `product_code`)
   * or 422 (out-of-domain enum value), `ApiError.status` carries the exact
   * upstream status — the form renders its own copy per status, never a
   * generic message.
   */
  createProduct(body: CreateProductRequest, signal?: AbortSignal): Promise<ProductConfig>;
  /** Flip `Product.is_active` false (Story N5 AC4) — never a delete. */
  retireProduct(productCode: string, signal?: AbortSignal): Promise<ProductConfig>;
  /**
   * The configured tenant's `tenant_products` rows (Story N3 AC2/AC3) — ALL
   * rows, including soft-disabled ones (`is_active: false` stays in the
   * list, never removed). The caller filters to `is_active` for the
   * "available to this tenant" view; the unfiltered list backs the N4
   * toggle grid's current-state lookup.
   */
  getTenantProductAvailability(signal?: AbortSignal): Promise<TenantProductAvailability[]>;
  /** Create/re-enable this tenant's availability for a product (Story N4 AC1). */
  enableTenantProduct(
    productCode: string,
    signal?: AbortSignal,
  ): Promise<TenantProductAvailability>;
  /** Soft-disable this tenant's availability for a product (Story N4 AC2) — never a delete. */
  disableTenantProduct(
    productCode: string,
    signal?: AbortSignal,
  ): Promise<TenantProductAvailability>;
}

/** The search body: drop null/blank optionals; `status` is always sent (`[]` = default scope). */
const buildLeadsSearchBody = (query: LeadsQuery): Record<string, unknown> => {
  const { status, ...rest } = query;
  const body: Record<string, unknown> = { status: status ?? [] };
  Object.entries(rest).forEach(([key, value]) => {
    if (value != null && value !== '') body[key] = value;
  });
  return body;
};

/** Serialise a ConversationsQuery into the BFF's `/api/conversations` query string. */
const buildConversationsQueryString = (query: ConversationsQuery): string => {
  const params = new URLSearchParams();
  // `funnel_stage` is a repeated param (multi-select), one entry per value.
  (query.funnel_stage ?? []).forEach((stage) => params.append('funnel_stage', stage));
  // Boolean toggles are sent ONLY when on — an absent param means "don't filter".
  if (query.has_error) params.set('has_error', 'true');
  if (query.paused) params.set('paused', 'true');
  if (query.cursor != null && query.cursor !== '') params.set('cursor', query.cursor);
  if (query.limit != null) params.set('limit', String(query.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Shared by detail/create/activate (product_code only). The list route
 * additionally needs `skill_name` — passed as an extra optional field so
 * this one builder still covers both shapes without duplicating it.
 * `product_code == null` OMITS the param — never sent as `"null"` or `""`
 * (E0-BO-CFG-S3; `SkillKey.product_code` is genuinely nullable).
 */
const buildSkillVersionsQueryString = (
  query: SkillVersionsQuery & { skill_name?: string },
): string => {
  const params = new URLSearchParams();
  if (query.skill_name != null) params.set('skill_name', query.skill_name);
  if (query.product_code != null) params.set('product_code', query.product_code);
  return `?${params.toString()}`;
};

/**
 * The list route's own builder — adds the filter/sort/pagination params on
 * top of `skill_name`/`product_code`. `undefined` on any of them omits the
 * param entirely (the route applies its own default), never sends `"undefined"`.
 */
const buildSkillVersionsListQueryString = (query: SkillVersionsListQuery): string => {
  const params = new URLSearchParams();
  params.set('skill_name', query.skill_name);
  if (query.product_code != null) params.set('product_code', query.product_code);
  if (query.is_active != null) params.set('is_active', String(query.is_active));
  if (query.config_version_prefix != null && query.config_version_prefix !== '') {
    params.set('config_version_prefix', query.config_version_prefix);
  }
  if (query.sort != null) params.set('sort', query.sort);
  if (query.limit != null) params.set('limit', String(query.limit));
  if (query.offset != null) params.set('offset', String(query.offset));
  return `?${params.toString()}`;
};

interface ApiClientOptions {
  /** Base path for the BFF API. Same-origin by default (single deployable). */
  baseUrl?: string;
}

/**
 * Best-effort extraction of FastAPI's `{ "detail": "…" }` from an error body.
 * Returns `null` on an empty / non-JSON / detail-less body (e.g. the BFF's 502
 * sends no body) so a parse failure never masks the real status. The body is
 * read once here; the caller has already decided the response is not ok.
 */
const readErrorBody = async (
  response: Response,
): Promise<{ detail: string | null; body: unknown }> => {
  try {
    const data: unknown = await response.json();
    const detail =
      data != null &&
      typeof data === 'object' &&
      'detail' in data &&
      typeof (data as { detail: unknown }).detail === 'string'
        ? (data as { detail: string }).detail
        : null;
    return { detail, body: data ?? null };
  } catch {
    // Empty / non-JSON body — fall through to a null detail/body.
  }
  return { detail: null, body: null };
};

export const createApiClient = (
  getToken: TokenGetter,
  options: ApiClientOptions = {},
): ApiClient => {
  const baseUrl = options.baseUrl ?? '';

  const request = async <T>(
    path: string,
    init: { method: 'GET' | 'POST' | 'PUT'; body?: unknown; signal?: AbortSignal },
  ): Promise<T> => {
    const token = await getToken();
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };
    if (init.body !== undefined) headers['Content-Type'] = 'application/json';

    const response = await fetch(`${baseUrl}${path}`, {
      method: init.method,
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: init.signal,
    });

    if (response.status === 401 || response.status === 403) {
      throw new UnauthorizedError(response.status);
    }
    if (!response.ok) {
      // Preserve the BFF's distinct write semantics: the caller maps on
      // `status` (422/409/404/502) and renders `detail` where the BFF set one.
      const { detail, body } = await readErrorBody(response);
      throw new ApiError(
        response.status,
        detail ?? `Request to ${path} failed (${response.status})`,
        detail,
        body,
      );
    }

    return (await response.json()) as T;
  };

  return {
    getHealthOverview: (signal) =>
      request<HealthOverview>('/api/health/overview', { method: 'GET', signal }),
    getHealthTrends: (signal) =>
      request<HealthTrends>('/api/health/trends', { method: 'GET', signal }),
    getHealthSource: (source, signal) =>
      request<SourceSnapshot>(`/api/health/${source}`, { method: 'GET', signal }),
    searchLeads: (query, signal) =>
      request<LeadListEnvelope>('/api/leads/search', {
        method: 'POST',
        body: buildLeadsSearchBody(query),
        signal,
      }),
    getLeadDetail: (leadId, signal) =>
      request<LeadDetail>(`/api/leads/${encodeURIComponent(leadId)}`, { method: 'GET', signal }),
    getLeadFacts: (leadId, signal) =>
      request<LeadFacts>(`/api/leads/${encodeURIComponent(leadId)}/facts`, {
        method: 'GET',
        signal,
      }),
    transitionLead: (leadId, body, signal) =>
      request<LeadTransitionResponse>(`/api/leads/${encodeURIComponent(leadId)}/transition`, {
        method: 'POST',
        body,
        signal,
      }),
    getConversations: (query, signal) =>
      request<ConversationListEnvelope>(
        `/api/conversations${buildConversationsQueryString(query)}`,
        { method: 'GET', signal },
      ),
    getConversationDetail: (threadId, signal) =>
      request<ConversationDetail>(`/api/conversations/${encodeURIComponent(threadId)}`, {
        method: 'GET',
        signal,
      }),
    getConversationMessages: (threadId, signal) =>
      request<MessageRow[]>(`/api/conversations/${encodeURIComponent(threadId)}/messages`, {
        method: 'GET',
        signal,
      }),
    getConversationDecisions: (threadId, signal) =>
      request<DecisionRow[]>(`/api/conversations/${encodeURIComponent(threadId)}/decisions`, {
        method: 'GET',
        signal,
      }),
    getConversationTraces: (threadId, signal) =>
      request<TraceListEnvelope>(`/api/conversations/${encodeURIComponent(threadId)}/traces`, {
        method: 'GET',
        signal,
      }),
    controlConversation: (threadId, body, signal) =>
      request<ConversationControlResult>(
        `/api/conversations/${encodeURIComponent(threadId)}/control`,
        { method: 'POST', body, signal },
      ),
    resetLead: (threadId, body, signal) =>
      request<ResetLeadResponse>(`/api/conversations/${encodeURIComponent(threadId)}/reset-lead`, {
        method: 'POST',
        body,
        signal,
      }),
    getLeadFormOptions: (signal) =>
      request<LeadFormOptions>('/api/leads/form-options', { method: 'GET', signal }),
    createLead: (body, signal) =>
      request<ManualLeadCreateResponse>('/api/leads', { method: 'POST', body, signal }),
    getSkillKeys: (signal) =>
      request<SkillKey[]>('/api/config/skills/keys', { method: 'GET', signal }),
    getSkillVersions: (query, signal) =>
      request<SkillVersionList>(`/api/config/skills${buildSkillVersionsListQueryString(query)}`, {
        method: 'GET',
        signal,
      }),
    getSkillVersionDetail: (skillName, configVersion, query, signal) =>
      request<SkillVersionDetail>(
        `/api/config/skills/${encodeURIComponent(skillName)}/versions/${encodeURIComponent(
          configVersion,
        )}${buildSkillVersionsQueryString(query)}`,
        { method: 'GET', signal },
      ),
    createSkillVersion: (skillName, body, signal) =>
      request<CreateSkillVersionResponse>(
        `/api/config/skills/${encodeURIComponent(skillName)}/versions`,
        { method: 'POST', body, signal },
      ),
    activateSkillVersion: (skillName, configVersion, body, signal) =>
      request<SkillVersionDetail>(
        `/api/config/skills/${encodeURIComponent(skillName)}/versions/${encodeURIComponent(
          configVersion,
        )}/activate`,
        { method: 'POST', body, signal },
      ),
    getNumberConfig: (signal) =>
      request<TenantNumberConfig>('/api/config/numbers', { method: 'GET', signal }),
    setNumberConfig: (body, signal) =>
      request<TenantNumberConfig>('/api/config/numbers', { method: 'PUT', body, signal }),
    getProductCatalogue: (signal) =>
      request<ProductConfig[]>('/api/config/products', { method: 'GET', signal }),
    createProduct: (body, signal) =>
      request<ProductConfig>('/api/config/products', { method: 'POST', body, signal }),
    retireProduct: (productCode, signal) =>
      request<ProductConfig>(`/api/config/products/${encodeURIComponent(productCode)}/retire`, {
        method: 'POST',
        body: {},
        signal,
      }),
    getTenantProductAvailability: (signal) =>
      request<TenantProductAvailability[]>('/api/config/products/availability', {
        method: 'GET',
        signal,
      }),
    enableTenantProduct: (productCode, signal) =>
      request<TenantProductAvailability>(
        `/api/config/products/${encodeURIComponent(productCode)}/enable`,
        { method: 'POST', body: {}, signal },
      ),
    disableTenantProduct: (productCode, signal) =>
      request<TenantProductAvailability>(
        `/api/config/products/${encodeURIComponent(productCode)}/disable`,
        { method: 'POST', body: {}, signal },
      ),
  };
};
